import React, { useState, useCallback } from "react";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface CompanyListFiltersProps {
  filters: {
    companyName?: string;
    intacctId?: string;
    salesforceId?: string;
  };
  onFilterChange: (filterKey: string, value: string | undefined) => void;
}

const CompanyListFilters: React.FC<CompanyListFiltersProps> = ({
  filters,
  onFilterChange,
}) => {
  const [companyNameFilter, setCompanyNameFilter] = useState(
    filters.companyName || ""
  );
  const [intacctIdFilter, setIntacctIdFilter] = useState(
    filters.intacctId || ""
  );
  const [salesforceIdFilter, setSalesforceIdFilter] = useState(
    filters.salesforceId || ""
  );

  const debouncedCompanyNameFilter = useDebouncedValue(companyNameFilter, 500);
  const debouncedIntacctIdFilter = useDebouncedValue(intacctIdFilter, 500);
  const debouncedSalesforceIdFilter = useDebouncedValue(
    salesforceIdFilter,
    500
  );

  React.useEffect(() => {
    onFilterChange("companyName", debouncedCompanyNameFilter || undefined);
  }, [debouncedCompanyNameFilter]);

  React.useEffect(() => {
    onFilterChange("intacctId", debouncedIntacctIdFilter || undefined);
  }, [debouncedIntacctIdFilter]);

  React.useEffect(() => {
    onFilterChange("salesforceId", debouncedSalesforceIdFilter || undefined);
  }, [debouncedSalesforceIdFilter]);

  const handleClearAllFilters = useCallback(() => {
    setCompanyNameFilter("");
    setIntacctIdFilter("");
    setSalesforceIdFilter("");
    onFilterChange("companyName", undefined);
    onFilterChange("intacctId", undefined);
    onFilterChange("salesforceId", undefined);
  }, [onFilterChange]);

  const hasActiveFilters =
    companyNameFilter || intacctIdFilter || salesforceIdFilter;

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {/* Company Name */}
        <div>
          <Label htmlFor="companyName">Company Name</Label>
          <Input
            id="companyName"
            className="mt-1.5 bg-white"
            onChange={(e) => setCompanyNameFilter(e.target.value)}
            placeholder="Search..."
            type="text"
            value={companyNameFilter}
          />
        </div>

        {/* Intacct ID */}
        <div>
          <Label htmlFor="intacctId">Intacct ID</Label>
          <Input
            id="intacctId"
            className="mt-1.5 bg-white"
            onChange={(e) => setIntacctIdFilter(e.target.value)}
            placeholder="Exact match..."
            type="text"
            value={intacctIdFilter}
          />
        </div>

        {/* Salesforce ID */}
        <div>
          <Label htmlFor="salesforceId">Salesforce ID</Label>
          <Input
            id="salesforceId"
            className="mt-1.5 bg-white"
            onChange={(e) => setSalesforceIdFilter(e.target.value)}
            placeholder="Exact match..."
            type="text"
            value={salesforceIdFilter}
          />
        </div>

        {/* Clear All Filters */}
        <div className="flex items-end">
          <Button
            variant="outline"
            onClick={handleClearAllFilters}
            disabled={!hasActiveFilters}
          >
            Clear Filters
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CompanyListFilters;
